import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Modal,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Checkbox,
  Fab,
} from "@mui/material";
import { Add, Edit, Delete, Close } from "@mui/icons-material";
import { useSnackbar } from "../../Snackbar/SnackbarContext";
import apiEndpoints from "../../../apiconfig";
import Autocomplete from "@mui/material/Autocomplete";
import DropDownField from "../../../DropDownField";

const GLASS_BG = "rgba(255, 255, 255, 0.4)";
const GLASS_BORDER = "rgba(255,255,255,0.5)";

const days = [
  { guid: "mon", name: "Monday" },
  { guid: "tue", name: "Tuesday" },
  { guid: "wed", name: "Wednesday" },
  { guid: "thu", name: "Thursday" },
  { guid: "fri", name: "Friday" },
  { guid: "sat", name: "Saturday" },
];


export default function TimetableApp() {
  const [items, setItems] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const { showSnackbar } = useSnackbar();

  const [subjectList, setSubjectList] = useState([]);
  const [branchList, setBranchList] = useState([]);
  const [classList, setClassList] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedClass, setSelectedClass] = useState("");


  useEffect(() => {
    if (staffList.length > 0) {
      fetchTimetable();
    }
  }, [staffList]);

const staffMap = useMemo(() => {
  const map = {};
  staffList.forEach((s) => {
    map[s.guid] = s.name;
  });
  return map;
}, [staffList]);

  const [form, setForm] = useState({
    branchId: "",
    branchName: "",
    classId: "",
    className: "",
    day: "",
    period_no: "",
    start_time: "",
    end_time: "",
    subject: "",
    staff: "",
    remarks: "",
    isActive: true,
  });

  const buildTableForClass = (group) => {
    const periodMap = new Map();

    group.rows.forEach((r) => {
      const p = Number(r.period_no);
      if (!periodMap.has(p)) {
        periodMap.set(p, {
          period_no: p,
          start_time: r.start_time,
          end_time: r.end_time,
        });
      }
    });

    const sortedPeriods = Array.from(periodMap.values()).sort(
      (a, b) => a.period_no - b.period_no
    );

    const lookup = {};
    days.forEach((d) => {
      lookup[d.guid] = {};
    });

    group.rows.forEach((r) => {
      if (lookup[r.day]) {
        lookup[r.day][Number(r.period_no)] = r;
      }
    });

    const lunchFullPeriods = new Set();
    sortedPeriods.forEach((p) => {
      const allLunch = days.every((d) => {
        const e = lookup[d.guid]?.[p.period_no];
        return e?.subject === "Lunch";
      });
      if (allLunch) lunchFullPeriods.add(p.period_no);
    });

    return { sortedPeriods, lookup, lunchFullPeriods };
  };


  const getClassNameByGuid = (guid) => {
    const f = classList.find((c) => c.guid === guid);
    return f ? f.name : "";
  };

  const getStaffNameByGuid = (guid) => {
    const f = staffList.find((s) => s.guid === guid);
    return f ? f.name : "";
  };

  // -----------------------------
  // FETCH BRANCHES ON LOAD
  // -----------------------------

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(
          `${apiEndpoints.dropdown}?type=branch&mode=initial`,
          {
            headers: token
              ? {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                }
              : { "Content-Type": "application/json" },
          }
        );
        const data = await res.json();
        if (data?.status === "success" && Array.isArray(data.data)) {
          setBranchList(data.data);
        } else setBranchList([]);
      } catch {
        setBranchList([]);
      }
    };

    fetchBranches();
  }, []);

  // -----------------------------
  // FETCH TIMETABLE
  // -----------------------------

  useEffect(() => {
    fetchTimetable();
  }, [selectedBranch, selectedClass]);

  const fetchTimetable = async () => {
    const token = localStorage.getItem("token");

    const res = await fetch(apiEndpoints.timetable, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    const data = await res.json();

    if (data.status === "success") {
     const mapped = data.data.records.map((r) => ({
       id: r.id,
       guid: r.guid,
       branchId: r.branch_guid,
       classId: r.class_guid,
       className: r.class_name || "",
       day: r.day,
       period_no: r.period_no,
       start_time: r.start_time,
       end_time: r.end_time,
       subject: r.subject,
       staffGuid: r.staff_guid,
       staffName: r.staff_name || "", // ✅ FIX
       remarks: r.remarks,
       isActive: r.isActive,
     }));


      setItems(mapped);
    } else {
      setItems([]);
    }
  };

  // -----------------------------
  // RESET FORM
  // -----------------------------
  const resetForm = () => {
    setForm({
      branchId: "",
      branchName: "",
      classId: "",
      className: "",
      day: "",
      period_no: "",
      start_time: "",
      end_time: "",
      subject: "",
      staff: "",
      remarks: "",
      isActive: true,
    });
    setErrors({});
    setEditingId(null);
  };

  const openAdd = () => {
    resetForm();
    setOpenModal(true);
  };

  // -----------------------------
  // FIXED openEdit() WITH AUTO-LOADING DROPDOWNS
  // -----------------------------

  const openEdit = async (entry) => {
    if (!entry) return;

    // 1️⃣ Load all dropdowns for this branch BEFORE filling form
    await onBranchSelect(entry.branchId);

    // 2️⃣ Now fill form with correct GUID values
    setForm({
      branchId: entry.branchId,
      branchName: branchList.find((b) => b.guid === entry.branchId)?.name || "",
      classId: entry.classId,
      className: getClassNameByGuid(entry.classId),
      day: entry.day,
      period_no: entry.period_no,
      start_time: entry.start_time,
      end_time: entry.end_time,
      subject: entry.subject,
      staff: entry.staffGuid || "",
      remarks: entry.remarks,
      isActive: entry.isActive ?? true,
    });

    setEditingId(entry.guid);
    setOpenModal(true);
  };

  const closeModal = () => {
    setOpenModal(false);
    setEditingId(null);
  };

  // -----------------------------
  // VALIDATION
  // -----------------------------
  const validateForm = () => {
    const t = {};
    if (!form.branchId) t.branchId = "Branch is required";
    if (!form.classId) t.classId = "Class is required";
    if (!form.day) t.day = "Day is required";
    if (!form.period_no) t.period_no = "Period number required";
    if (!form.start_time) t.start_time = "Start time required";
    if (!form.end_time) t.end_time = "End time required";
    if (!form.subject) t.subject = "Subject required";
    if (form.subject !== "Lunch" && !form.staff) t.staff = "Staff required";

    setErrors(t);
    return Object.keys(t).length === 0;
  };

  // -----------------------------
  // SAVE ENTRY
  // -----------------------------

  const onSave = async () => {
    if (!validateForm()) {
      showSnackbar("Please fill required fields", "error");
      return;
    }

    const token = localStorage.getItem("token");

    const payload = {
      guid: editingId,
      branch_guid: form.branchId,
      class_guid: form.classId,
      day: form.day,
      period_no: Number(form.period_no),
      start_time: form.start_time,
      end_time: form.end_time,
      subject: form.subject,
      staff_guid: form.staff || "",
      room_no: "",
      remarks: form.remarks,
      isActive: form.isActive ? 1 : 0,
    };

    const method = editingId ? "PUT" : "POST";

    const res = await fetch(apiEndpoints.timetable, {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
      },
      body: JSON.stringify(payload),
    });

    const data = await res.json();

    if (data.status === "success") {
      showSnackbar(editingId ? "Updated" : "Created", "success");
      closeModal();
      fetchTimetable();
    } else showSnackbar(data.message, "error");
  };

  // -----------------------------
  // DELETE ENTRY
  // -----------------------------

  const onDelete = async (guid) => {
    if (!window.confirm("Delete this entry?")) return;

    const token = localStorage.getItem("token");

    const res = await fetch(`${apiEndpoints.timetable}?guid=${guid}`, {
      method: "DELETE",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    const data = await res.json();

    if (data.status === "success") {
      showSnackbar("Deleted", "success");
      fetchTimetable();
    } else showSnackbar(data.message, "error");
  };

  // -----------------------------
  // GROUP DATA BY CLASS
  // -----------------------------
  const grouped = useMemo(() => {
    const map = new Map();
    items.forEach((it) => {
      const key = String(it.classId);
      if (!map.has(key))
        map.set(key, {
          classId: it.classId,
          className: it.className || "No Class",
          rows: [],
        });
      map.get(key).rows.push(it);
    });
    return Array.from(map.values());
  }, [items]);

  // -----------------------------
  // FIXED: onBranchSelect RETURNS PROMISE
  // -----------------------------

  const onBranchSelect = async (selectedBranchGuid) => {
    return new Promise(async (resolve) => {
      setForm((f) => ({
        ...f,
        branchId: selectedBranchGuid,
        classId: "",
        className: "",
        staff: "",
      }));

      const token = localStorage.getItem("token");

      try {
        const classRes = await fetch(
          `${apiEndpoints.dropdown}?type=class&branch_guid=${selectedBranchGuid}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        const classData = await classRes.json();
        setClassList(classData.status === "success" ? classData.data : []);
      } catch {
        setClassList([]);
      }

      try {
        const staffRes = await fetch(
          `${apiEndpoints.dropdown}?type=staff&branch_guid=${selectedBranchGuid}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        const staffData = await staffRes.json();
        setStaffList(staffData.status === "success" ? staffData.data : []);
      } catch {
        setStaffList([]);
      }

      try {
        const subjectRes = await fetch(
          `${apiEndpoints.dropdown}?type=subject&branch_guid=${selectedBranchGuid}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        const subjectData = await subjectRes.json();
        setSubjectList(
          subjectData.status === "success" ? subjectData.data : []
        );
      } catch {
        setSubjectList([]);
      }

      resolve(true);
    });
  };

  // -----------------------------
  // UI BELOW (unchanged)
  // -----------------------------

  return (
    <Box
      sx={{
        minHeight: "100vh",
        p: { xs: 2, md: 4 },
        background: "rgba(255,255,255,0.4)",
        borderRadius: 5,
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: "auto" }}>
        {/* TITLE + BUTTON */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 1,
            background: GLASS_BG,
            border: `1px solid ${GLASS_BORDER}`,
            mb: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              TIME TABLES
            </Typography>

            <Fab
              color="primary"
              size="medium"
              sx={{
                boxShadow: "0 6px 18px rgba(0,0,0,0.20)",
              }}
              onClick={openAdd}
            >
              <Add />
            </Fab>
          </Box>
        </Paper>

        {grouped.length === 0 ? (
          <Typography>No data</Typography>
        ) : (
          grouped.map((group) => {
            const { sortedPeriods, lookup, lunchFullPeriods } =
              buildTableForClass(group);

            return (
              <Box key={String(group.classId)} sx={{ mb: 5 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  {group.className}
                </Typography>

                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    p: 1.5,
                    background: "transparent",
                    border: `1px solid rgba(0,0,0,0.06)`,
                    overflowX: "auto",
                  }}
                >
                  <table
                    style={{
                      borderCollapse: "collapse",
                      width: "100%",
                      minWidth: 800,
                      background: "rgba(255, 255, 255, 0.5)",
                      backdropFilter: "blur(16px)",
                      border: "1px solid rgba(255,255,255,0.5)",
                      boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
                    }}
                  >
                    <thead>
                      <tr>
                        <th
                          style={{
                            textAlign: "left",
                            padding: 12,
                            border: "1px solid rgba(0,0,0,0.08)",
                            background: "rgba(255,255,255,0.4)",
                          }}
                        >
                          Day / Period
                        </th>

                        {sortedPeriods.map((p) => (
                          <th
                            key={p.period_no}
                            style={{
                              textAlign: "center",
                              padding: 12,
                              border: "1px solid rgba(0,0,0,0.08)",
                              background: "rgba(255,255,255,0.4)",
                              whiteSpace: "nowrap",
                              minWidth: 120,
                            }}
                          >
                            <div style={{ fontWeight: 800 }}>
                              P {p.period_no}
                            </div>
                            <div style={{ fontSize: 12, opacity: 0.8 }}>
                              {`${p.start_time}-${p.end_time}`}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>

                    <tbody>
                      {days.map((d, rowIndex) => (
                        <tr key={d.guid}>
                          <td
                            style={{
                              padding: 12,
                              border: "1px solid rgba(0,0,0,0.08)",
                              fontWeight: 700,
                              background: "rgba(255,255,255,0.4)",
                            }}
                          >
                            {d.name}
                          </td>

                          {sortedPeriods.map((p) => {
                            if (lunchFullPeriods.has(p.period_no)) {
                              if (rowIndex === 0) {
                                return (
                                  <td
                                    key={p.period_no + "_lunch_full"}
                                    rowSpan={days.length}
                                    style={{
                                      padding: 12,
                                      border: "1px solid rgba(0,0,0,0.08)",
                                      minWidth: 160,
                                      textAlign: "center",
                                      background: "rgba(0,0,0,0.04)",
                                      fontWeight: 800,
                                    }}
                                  >
                                    LUNCH
                                    <div
                                      style={{
                                        fontSize: 12,
                                        opacity: 0.8,
                                        marginTop: 6,
                                      }}
                                    >
                                      {p.start_time} - {p.end_time}
                                    </div>
                                  </td>
                                );
                              }
                              return null;
                            }

                            const entry = lookup[d.guid]?.[p.period_no];

                            if (!entry) {
                              return (
                                <td
                                  key={p.period_no + "_" + d.guid}
                                  style={{
                                    padding: 12,
                                    border: "1px solid rgba(0,0,0,0.08)",
                                    minWidth: 140,
                                    textAlign: "center",
                                  }}
                                />
                              );
                            }

                            if (entry.subject === "Lunch") {
                              return (
                                <td
                                  key={p.period_no + "_" + d.guid}
                                  style={{
                                    padding: 12,
                                    border: "1px solid rgba(0,0,0,0.08)",
                                    minWidth: 140,
                                    textAlign: "center",
                                    background: "rgba(0,0,0,0.04)",
                                    fontWeight: 700,
                                  }}
                                >
                                  LUNCH
                                  <div
                                    style={{ fontSize: 12, opacity: 0.7 }}
                                  >{`${entry.start_time}-${entry.end_time}`}</div>
                                </td>
                              );
                            }

                            return (
                              <td
                                key={p.period_no + "_" + d.guid}
                                style={{
                                  padding: 12,
                                  border: "1px solid rgba(0,0,0,0.08)",
                                  minWidth: 140,
                                  background: "rgba(255,255,255,0.4)",
                                  verticalAlign: "top",
                                }}
                              >
                                <div style={{ fontWeight: 700 }}>
                                  {entry.subject}
                                </div>
                                <div style={{ fontSize: 13, opacity: 0.85 }}>
                                  {entry.staffName}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Paper>

                <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {group.rows.map((r) => (
                    <Paper
                      key={r.id}
                      sx={{
                        p: 1,
                        display: "flex",
                        alignItems: "center",
                        background: "rgba(255,255,255,0.4)",
                        gap: 1,
                        position: "relative",
                        zIndex: 50,
                      }}
                    >
                      <Typography sx={{ fontSize: 12, flex: 1 }}>
                        {days.find((d) => d.guid === r.day)?.name} • P
                        {r.period_no} • {r.subject}
                      </Typography>

                      <IconButton size="small" onClick={() => openEdit(r)}>
                        <Edit fontSize="small" />
                      </IconButton>

                      <IconButton size="small" onClick={() => onDelete(r.guid)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Paper>
                  ))}
                </Box>
              </Box>
            );
          })
        )}

        {/* Modal form */}
        {openModal && (
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: 2500,
              display: "flex",
              justifyContent: "flex-end",
              bgcolor: "rgba(15, 23, 42, 0.25)", // darker overlay
              backdropFilter: "blur(4px)", // strong blur
              WebkitBackdropFilter: "blur(18px)", // Safari support
              transition: "all 0.3s ease",
            }}
            onClick={closeModal}
          >
            {/* DRAWER PANEL */}
            <Box
              onClick={(e) => e.stopPropagation()}
              sx={{
                width: { xs: "100%", sm: "85%", md: "60%", lg: "45%" },
                height: "100vh",
                position: "relative",
                p: 3,
                flexDirection: "column",
              }}
            >
              <Paper
                elevation={6}
                sx={{
                  position: "relative",
                  width: "100%",
                  height: "100%",
                  borderRadius: 4,
                  p: { xs: 3, md: 4 },
                  bgcolor: "rgba(255,255,255,0.96)",
                  backdropFilter: "blur(18px)",
                  boxShadow:
                    "0 18px 45px rgba(15, 23, 42, 0.35), 0 0 0 1px rgba(148, 163, 184, 0.18)",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  animation: "slideIn 0.35s ease",
                }}
              >
                {/* CLOSE BUTTON */}
                <IconButton
                  onClick={closeModal}
                  sx={{
                    position: "absolute",
                    top: 20,
                    right: 20,
                    bgcolor: "rgba(15,23,42,0.05)",
                    "&:hover": { bgcolor: "rgba(15,23,42,0.12)" },
                  }}
                >
                  <Close />
                </IconButton>

                {/* TITLE */}
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, pr: 4 }}>
                  {editingId
                    ? "Edit Timetable Entry"
                    : "Create Timetable Entry"}
                </Typography>

                {/* FORM FIELDS */}
                <Grid container spacing={2} sx={{ overflowY: "auto" }}>
                  {/* BRANCH */}
                  <Grid item xs={12} sm={6}>
                    <DropDownField
                      label="Branch *"
                      value={form.branchId}
                      options={branchList} // [{ guid, name }]
                      error={Boolean(errors.branchId)}
                      onChange={(guid) => {
                        onBranchSelect(guid);
                        setSelectedBranch(guid);
                        setErrors((e) => ({ ...e, branchId: "" }));
                      }}
                    />
                  </Grid>

                  {/* CLASS */}
                  <Grid item xs={12} sm={6}>
                    <DropDownField
                      label="Class *"
                      value={form.classId}
                      options={classList} // [{ guid, name }]
                      error={Boolean(errors.classId)}
                      onChange={(guid) => {
                        setForm((f) => ({
                          ...f,
                          classId: guid,
                          className:
                            classList.find((c) => c.guid === guid)?.name || "",
                        }));
                        setSelectedClass(guid);
                        setErrors((e) => ({ ...e, classId: "" }));
                      }}
                    />
                  </Grid>

                  {/* DAY */}
                  <Grid item xs={12} sm={6}>
                    <DropDownField
                      label="Day *"
                      value={form.day}
                      options={days}
                      error={Boolean(errors.day)}
                      onChange={(value) => {
                        setForm((f) => ({ ...f, day: value }));
                        setErrors((e) => ({ ...e, day: "" }));
                      }}
                    />
                  </Grid>

                  {/* PERIOD */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Period No"
                      type="number"
                      fullWidth
                      value={form.period_no}
                      onChange={(e) => {
                        setForm({ ...form, period_no: e.target.value });
                        setErrors({ ...errors, period_no: "" });
                      }}
                      error={!!errors.period_no}
                      helperText={errors.period_no}
                      InputProps={{ sx: { borderRadius: 3 } }}
                    />
                  </Grid>

                  {/* START TIME */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Start Time"
                      type="time"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={form.start_time}
                      onChange={(e) => {
                        setForm({ ...form, start_time: e.target.value });
                        setErrors({ ...errors, start_time: "" });
                      }}
                      error={!!errors.start_time}
                      helperText={errors.start_time}
                      InputProps={{ sx: { borderRadius: 3 } }}
                    />
                  </Grid>

                  {/* END TIME */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="End Time"
                      type="time"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={form.end_time}
                      onChange={(e) => {
                        setForm({ ...form, end_time: e.target.value });
                        setErrors({ ...errors, end_time: "" });
                      }}
                      error={!!errors.end_time}
                      helperText={errors.end_time}
                      InputProps={{ sx: { borderRadius: 3 } }}
                    />
                  </Grid>

                  {/* SUBJECT */}
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      freeSolo
                      options={subjectList.map((s) => s.name)}
                      value={form.subject}
                      onChange={(event, newValue) => {
                        setForm({ ...form, subject: newValue });
                        setErrors({ ...errors, subject: "" });
                      }}
                      onInputChange={(event, newInput) => {
                        setForm({ ...form, subject: newInput });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Subject"
                          error={!!errors.subject}
                          helperText={errors.subject}
                          fullWidth
                          InputProps={{ sx: { borderRadius: 3 } }}
                        />
                      )}
                    />
                  </Grid>

                  {/* STAFF */}
                  <Grid item xs={12} sm={6}>
                    <DropDownField
                      label="Staff"
                      value={form.staff}
                      options={staffList} // [{ guid, name }]
                      error={Boolean(errors.staff)}
                      onChange={(guid) => {
                        setForm((f) => ({ ...f, staff: guid }));
                        setErrors((e) => ({ ...e, staff: "" }));
                      }}
                    />
                  </Grid>

                  {/* REMARKS */}
                  <Grid item xs={12}>
                    <TextField
                      label="Remarks"
                      fullWidth
                      multiline
                      minRows={2}
                      value={form.remarks}
                      onChange={(e) =>
                        setForm({ ...form, remarks: e.target.value })
                      }
                      InputProps={{ sx: { borderRadius: 3 } }}
                    />
                  </Grid>

                  {/* ACTIVE */}
                  <Grid item xs={12}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Checkbox
                        checked={!!form.isActive}
                        onChange={(e) =>
                          setForm({ ...form, isActive: e.target.checked })
                        }
                      />
                      <Typography>Active</Typography>
                    </Stack>
                  </Grid>

                  {/* BUTTONS */}
                  <Grid item xs={12} sx={{ width: "100%" }}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={2}
                      sx={{ width: "100%" }}
                    >
                      <Button
                        variant="contained"
                        onClick={onSave}
                        sx={{
                          flex: 1,
                          borderRadius: 999,
                          textTransform: "none",
                          px: 3,
                          minWidth: "120px",
                        }}
                      >
                        {editingId ? "Update" : "Create"}
                      </Button>

                      <Button
                        variant="outlined"
                        onClick={closeModal}
                        sx={{
                          flex: 1,
                          borderRadius: 999,
                          textTransform: "none",
                          px: 3,
                          minWidth: "120px",
                        }}
                      >
                        Cancel
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </Paper>
            </Box>

            {/* ANIMATIONS */}
            <style>
              {`
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}
            </style>
          </Box>
        )}
      </Box>
    </Box>
  );
}
